import type {
  AdminAnalyticsData,
  PaginatedResponse,
  InventoryItem,
  ItemImage,
  InventoryQuery,
  CreateInventoryItemInput,
  UpdateInventoryItemInput,
  ApiError,
  ItemLinkWithDetails,
  DeletePreflightResult,
  CollectionHealthSummary,
  PublicProfile,
  WantedItem,
  Offer,
  Notification,
  Favorite,
  UpdatePublicProfileInput,
  UpdateListingStatusInput,
  CreateWantedItemInput,
  UpdateWantedItemInput,
  CreateOfferInput,
  CustomVaultDefinitionInput,
  CustomVaultDefinition,
} from '@siliconvault/shared';

export interface ShowroomTickerItem {
  id: string;
  name: string;
  platform: string;
  category: string;
  subcategory: string | null;
  condition: string;
  ownerDisplayName: string | null;
  acquisitionPrice: string | null;
  currency: string;
  updatedAt: string;
}

export interface TaxonomySuggestionsResponse {
  suggestions: string[];
}

const BASE_URL = '/api';

const IMG_MAX_PX = 1600;
const IMG_QUALITY = 0.85;

/** Resize a File/Blob image to fit within IMG_MAX_PX and compress as JPEG. */
function resizeImageBlob(blob: File | Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      let { width, height } = img;
      if (width <= IMG_MAX_PX && height <= IMG_MAX_PX && blob.size <= 3 * 1024 * 1024) {
        return resolve(blob);
      }
      if (width > IMG_MAX_PX || height > IMG_MAX_PX) {
        const scale = IMG_MAX_PX / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas not supported'));
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Compression failed'))),
        'image/jpeg',
        IMG_QUALITY,
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(blob);
  });
}

class ApiClient {
  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${BASE_URL}${path}`;

    const headers: Record<string, string> = {};
    if (options.body) {
      headers['Content-Type'] = 'application/json';
    }
    Object.assign(headers, options.headers);

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers,
    });

    if (response.status === 401) {
      if (path === '/auth/login' || path === '/auth/register') {
        // Login/register failure — surface the real error from the server
        const error = await response.json();
        throw error as ApiError;
      }

      const refreshed = await this.refreshToken();
      if (refreshed) {
        const retryResponse = await fetch(url, {
          ...options,
          credentials: 'include',
          headers,
        });

        if (!retryResponse.ok) {
          const error = await retryResponse.json();
          throw error as ApiError;
        }
        return retryResponse.json();
      }

      throw { statusCode: 401, error: 'Unauthorized', message: 'Session expired' } as ApiError;
    }

    if (response.status === 204) {
      return undefined as T;
    }

    if (!response.ok) {
      const error = await response.json();
      throw error as ApiError;
    }

    return response.json();
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  private generateIdempotencyKey(): string {
    return crypto.randomUUID();
  }

  // Vaults
  async listVaults() {
    return this.request<{ data: Array<{
      id: string;
      userId: string;
      vaultType: string;
      name: string;
      slug: string | null;
      customDefinition: CustomVaultDefinition | null;
      isPublic: boolean;
      createdAt: string;
      updatedAt: string;
    }> }>('/vaults');
  }

  async createVault(vaultType: string, name: string) {
    return this.request<{
      id: string;
      userId: string;
      vaultType: string;
      name: string;
      slug: string | null;
      customDefinition: CustomVaultDefinition | null;
      isPublic: boolean;
      createdAt: string;
      updatedAt: string;
    }>('/vaults', {
      method: 'POST',
      body: JSON.stringify({ vaultType, name }),
    });
  }

  async createCustomVault(name: string, customDefinition: CustomVaultDefinitionInput) {
    return this.request<{
      id: string;
      userId: string;
      vaultType: string;
      name: string;
      slug: string | null;
      customDefinition: CustomVaultDefinition | null;
      isPublic: boolean;
      createdAt: string;
      updatedAt: string;
    }>('/vaults', {
      method: 'POST',
      body: JSON.stringify({ kind: 'custom', name, customDefinition }),
    });
  }

  async getVault(vaultId: string) {
    return this.request<{
      id: string;
      userId: string;
      vaultType: string;
      name: string;
      slug: string | null;
      isPublic: boolean;
      createdAt: string;
      updatedAt: string;
    }>(`/vaults/${vaultId}`);
  }

  async updateVaultDefinition(vaultId: string, customDefinition: CustomVaultDefinitionInput) {
    return this.request<{
      id: string;
      vaultType: string;
      customDefinition: CustomVaultDefinition | null;
    }>(`/vaults/${vaultId}`, {
      method: 'PATCH',
      body: JSON.stringify({ customDefinition }),
    });
  }

  async updateVault(vaultId: string, data: { name?: string; slug?: string | null; isPublic?: boolean }) {
    return this.request<{
      id: string;
      userId: string;
      vaultType: string;
      name: string;
      slug: string | null;
      isPublic: boolean;
      createdAt: string;
      updatedAt: string;
    }>(`/vaults/${vaultId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteVault(vaultId: string) {
    return this.request<void>(`/vaults/${vaultId}`, {
      method: 'DELETE',
    });
  }

  async listVaultTypes() {
    return this.request<{ data: Array<{
      id: string;
      label: string;
      tagline: string;
      icon: string;
      categories: string[];
      brands: string[];
      specializedFeatures: Record<string, boolean>;
    }> }>('/vault-types');
  }

  // Collection Sets
  async listSets(vaultId?: string) {
    const qs = vaultId ? `?vaultId=${vaultId}` : '';
    return this.request<{
      data: Array<{
        id: string;
        name: string;
        description: string;
        platform: string | null;
        totalItems: number;
        ownedItems: number;
        completionPercent: number;
        createdAt: string;
        updatedAt: string;
      }>;
    }>(`/sets${qs}`);
  }

  async getSetTemplates(vaultType?: string) {
    const qs = vaultType ? `?vaultType=${vaultType}` : '';
    return this.request<{
      data: Array<{ name: string; platform: string; items: string[] }>;
    }>(`/sets/templates${qs}`);
  }

  async createSet(data: {
    name: string;
    description?: string;
    platform?: string;
    items?: Array<{ name: string; platform?: string; category?: string }>;
  }) {
    return this.request<{
      id: string;
      name: string;
      description: string;
      platform: string | null;
      totalItems: number;
      ownedItems: number;
      completionPercent: number;
      createdAt: string;
      updatedAt: string;
    }>('/sets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSetDetail(setId: string) {
    return this.request<{
      id: string;
      name: string;
      description: string;
      platform: string | null;
      totalItems: number;
      ownedItems: number;
      completionPercent: number;
      createdAt: string;
      updatedAt: string;
      items: Array<{
        id: string;
        name: string;
        platform: string;
        category: string;
        isOwned: boolean;
        inventoryItemId: string | null;
        inventoryItemImageUrl: string | null;
        sortOrder: number;
      }>;
    }>(`/sets/${setId}`);
  }

  async updateSet(setId: string, data: { name?: string; description?: string }) {
    return this.request<any>(`/sets/${setId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSet(setId: string) {
    return this.request<void>(`/sets/${setId}`, { method: 'DELETE' });
  }

  async addSetItem(setId: string, data: { name: string; platform?: string; category?: string }) {
    return this.request<any>(`/sets/${setId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeSetItem(setId: string, itemId: string) {
    return this.request<void>(`/sets/${setId}/items/${itemId}`, { method: 'DELETE' });
  }

  async toggleSetItemOwned(setId: string, itemId: string, isOwned: boolean, inventoryItemId?: string | null) {
    return this.request<{ success: boolean }>(`/sets/${setId}/items/${itemId}/owned`, {
      method: 'PATCH',
      body: JSON.stringify({ isOwned, inventoryItemId }),
    });
  }

  async autoMatchSetItems(setId: string) {
    return this.request<{ matchCount: number }>(`/sets/${setId}/auto-match`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Duplicates
  async scanDuplicates(vaultId?: string) {
    const qs = vaultId ? `?vaultId=${vaultId}` : '';
    return this.request<{
      data: Array<{
        item1: { id: string; name: string; category: string; platform: string; condition: string; imageUrl: string | null; createdAt: string };
        item2: { id: string; name: string; category: string; platform: string; condition: string; imageUrl: string | null; createdAt: string };
        similarity: number;
      }>;
    }>(`/duplicates${qs}`);
  }

  async dismissDuplicate(item1Id: string, item2Id: string) {
    return this.request<{ success: boolean }>('/duplicates/dismiss', {
      method: 'POST',
      body: JSON.stringify({ item1Id, item2Id }),
    });
  }

  async mergeDuplicates(keepItemId: string, removeItemId: string) {
    return this.request<{ success: boolean }>('/duplicates/merge', {
      method: 'POST',
      body: JSON.stringify({ keepItemId, removeItemId }),
    });
  }

  // Messaging
  async listConversations() {
    return this.request<{
      data: Array<{
        id: string;
        otherUserId: string;
        otherUserDisplayName: string;
        otherUserAvatarUrl: string | null;
        otherUserProfileSlug: string | null;
        lastMessage: string;
        lastMessageAt: string;
        unreadCount: number;
      }>;
    }>('/messages');
  }

  async getConversationMessages(conversationId: string, query: Record<string, string> = {}) {
    const params = new URLSearchParams(query);
    const qs = params.toString();
    return this.request<{
      data: Array<{
        id: string;
        conversationId: string;
        senderId: string;
        content: string;
        read: boolean;
        createdAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/messages/${conversationId}${qs ? `?${qs}` : ''}`);
  }

  async sendMessage(userId: string, content: string) {
    return this.request<{
      id: string;
      conversationId: string;
      senderId: string;
      content: string;
      read: boolean;
      createdAt: string;
    }>(`/messages/${userId}`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async markConversationRead(conversationId: string) {
    return this.request<{ success: boolean }>(`/messages/${conversationId}/read`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async getUnreadMessageCount() {
    return this.request<{ unreadCount: number }>('/messages/unread-count');
  }

  // Activity Feed
  async listActivityFeed(query: Record<string, string> = {}) {
    const params = new URLSearchParams(query);
    const qs = params.toString();
    return this.request<{
      data: Array<{
        id: string;
        type: string;
        userId: string;
        userDisplayName: string;
        userAvatarUrl: string | null;
        userProfileSlug: string | null;
        data: Record<string, unknown>;
        createdAt: string;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/activity${qs ? `?${qs}` : ''}`);
  }

  // Collection Stats
  async getCollectionStats(vaultId?: string) {
    const qs = vaultId ? `?vaultId=${vaultId}` : '';
    return this.request<{
      summary: {
        totalItems: number;
        totalValue: string;
        uniquePlatforms: number;
        uniqueCategories: number;
        oldestItem: string | null;
        newestItem: string | null;
      };
      platformDistribution: Array<{ platform: string; count: number }>;
      conditionBreakdown: Array<{ condition: string; count: number }>;
      acquisitionTimeline: Array<{ month: string; count: number }>;
      valueByCategory: Array<{ category: string; value: string }>;
    }>(`/stats${qs}`);
  }

  // Auth
  async register(data: {
    email: string;
    password: string;
    displayName: string;
  }) {
    return this.request<{
      user: { id: string; email: string; displayName: string; role: string };
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async login(data: { email: string; password: string }) {
    return this.request<{
      user: { id: string; email: string; displayName: string; role: string };
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async logout() {
    return this.request<{ message: string }>('/auth/logout', {
      method: 'POST',
    });
  }

  async getMe() {
    return this.request<{
      userId: string;
      role: string;
      displayName: string;
      email: string;
      preferences: { theme: string; defaultView: string; itemsPerPage: number };
    }>('/auth/me');
  }

  // Profile
  async getProfile() {
    return this.request<{
      id: string;
      email: string;
      displayName: string;
      role: string;
      preferences: { theme: string; defaultView: string; itemsPerPage: number };
      createdAt: string;
    }>('/profile');
  }

  async updateProfile(data: { email?: string; displayName?: string }) {
    return this.request<{
      id: string;
      email: string;
      displayName: string;
      role: string;
      preferences: { theme: string; defaultView: string; itemsPerPage: number };
      createdAt: string;
    }>('/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data: { currentPassword: string; newPassword: string }) {
    return this.request<{ success: boolean }>('/profile/password', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getPreferences() {
    return this.request<{ theme: string; defaultView: string; itemsPerPage: number; locale: string; showTicker: boolean }>('/profile/preferences');
  }

  async updatePreferences(data: { theme?: string; defaultView?: string; itemsPerPage?: number; showTicker?: boolean }) {
    return this.request<{ theme: string; defaultView: string; itemsPerPage: number; showTicker: boolean }>('/profile/preferences', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Inventory
  async listInventory(query: Partial<InventoryQuery> = {}) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== false) {
        params.set(key, String(value));
      }
    });
    const qs = params.toString();
    return this.request<PaginatedResponse<InventoryItem>>(
      `/inventory${qs ? `?${qs}` : ''}`
    );
  }

  async getInventoryItem(itemId: string) {
    return this.request<InventoryItem>(`/inventory/${itemId}`);
  }

  async createInventoryItem(data: CreateInventoryItemInput, vaultId?: string) {
    const qs = vaultId ? `?vaultId=${vaultId}` : '';
    return this.request<InventoryItem>(`/inventory${qs}`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Idempotency-Key': this.generateIdempotencyKey(),
      },
    });
  }

  async updateInventoryItem(itemId: string, data: UpdateInventoryItemInput) {
    return this.request<InventoryItem>(`/inventory/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Idempotency-Key': this.generateIdempotencyKey(),
      },
    });
  }

  async deleteInventoryItem(itemId: string) {
    return this.request<void>(`/inventory/${itemId}`, {
      method: 'DELETE',
    });
  }

  async analyzeImage(fileOrBlob: File | Blob, contentType?: string, vaultType?: string) {
    const buffer = await fileOrBlob.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );

    const type = contentType || (fileOrBlob instanceof File ? fileOrBlob.type : 'image/jpeg');

    return this.request<{
      name: string;
      category: string;
      platform: string;
      subcategory: string | null;
      condition: string;
      videoSystem: string | null;
      description: string;
      confidence: 'high' | 'medium' | 'low';
      cpu?: string | null;
      memory?: string | null;
      launchDate?: string | null;
      launchPrice?: string | null;
      launchPriceCurrency?: string | null;
      launchPriceInflationAdjusted?: string | null;
    }>('/inventory/analyze-image', {
      method: 'POST',
      body: JSON.stringify({ data: base64, contentType: type, vaultType }),
    });
  }

  async uploadItemImage(itemId: string, fileOrBlob: File | Blob, contentType?: string) {
    const resized = await resizeImageBlob(fileOrBlob);
    const buffer = await resized.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );

    const type = resized.type || contentType || 'image/jpeg';
    const name = fileOrBlob instanceof File
      ? fileOrBlob.name.replace(/\.\w+$/, '.jpg')
      : 'edited-image.jpg';

    return this.request<{ imageUrl: string }>(`/inventory/${itemId}/image`, {
      method: 'POST',
      body: JSON.stringify({
        data: base64,
        contentType: type,
        filename: name,
      }),
    });
  }

  // Item Images (multi-image gallery)
  async listItemImages(itemId: string) {
    return this.request<{ data: ItemImage[] }>(`/inventory/${itemId}/images`);
  }

  async uploadItemImageMulti(itemId: string, fileOrBlob: File | Blob, contentType?: string) {
    const resized = await resizeImageBlob(fileOrBlob);
    const buffer = await resized.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );

    const type = resized.type || contentType || 'image/jpeg';
    const name = fileOrBlob instanceof File
      ? fileOrBlob.name.replace(/\.\w+$/, '.jpg')
      : 'image.jpg';

    return this.request<ItemImage>(`/inventory/${itemId}/images`, {
      method: 'POST',
      body: JSON.stringify({ data: base64, contentType: type, filename: name }),
    });
  }

  async deleteItemImageById(itemId: string, imageId: string) {
    return this.request<void>(`/inventory/${itemId}/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  async setItemImagePrimary(itemId: string, imageId: string) {
    return this.request<{ success: boolean }>(`/inventory/${itemId}/images/${imageId}/primary`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async reorderItemImages(itemId: string, orderedIds: string[]) {
    return this.request<{ success: boolean }>(`/inventory/${itemId}/images/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ orderedIds }),
    });
  }

  // Admin
  async listUsers(query: Record<string, string | number> = {}) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    });
    const qs = params.toString();
    return this.request<PaginatedResponse<{
      id: string;
      email: string;
      displayName: string;
      role: 'admin' | 'user';
      aiTier: 'free' | 'pro';
      createdAt: string;
      lastLoginAt: string | null;
      itemCount: number;
      totalValue: string;
    }>>(`/admin/users${qs ? `?${qs}` : ''}`);
  }

  async getAnalytics(months = 12) {
    return this.request<{ data: AdminAnalyticsData }>(`/admin/analytics?months=${months}`);
  }

  async getStats() {
    return this.request<{
      data: {
        totalUsers: number;
        newThisMonth: number;
        totalItems: number;
        totalValue: string;
        forTrade: number;
        forSale: number;
        pendingOffers: number;
        libraryFiles: number;
        librarySize: string;
        criticalAlerts: number;
        unresolvedAlerts: number;
        activeRetrobright: number;
      };
    }>('/admin/stats');
  }

  async listUserInventory(userId: string, query: Record<string, string | number> = {}) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    });
    const qs = params.toString();
    return this.request<PaginatedResponse<InventoryItem>>(
      `/admin/users/${userId}/inventory${qs ? `?${qs}` : ''}`
    );
  }

  async listAllOffers(query: Record<string, string | number> = {}) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    });
    const qs = params.toString();
    return this.request<PaginatedResponse<{
      id: string;
      fromUserId: string;
      toUserId: string;
      fromName: string;
      toName: string;
      status: string;
      message: string;
      moneyAmount: string | null;
      moneyCurrency: string | null;
      offeredCount: number;
      requestedCount: number;
      createdAt: string;
      updatedAt: string;
    }>>(`/admin/offers${qs ? `?${qs}` : ''}`);
  }

  async forceWithdrawOffer(offerId: string) {
    return this.request<{ success: boolean }>(`/admin/offers/${offerId}/force-withdraw`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async inviteUser(data: {
    email: string;
    password: string;
    displayName: string;
    role: 'admin' | 'user';
  }) {
    return this.request<{
      id: string;
      email: string;
      displayName: string;
      role: 'admin' | 'user';
      createdAt: string;
    }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUserRole(userId: string, role: 'admin' | 'user') {
    return this.request<{
      id: string;
      email: string;
      displayName: string;
      role: 'admin' | 'user';
      createdAt: string;
    }>(`/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async updateUserTier(userId: string, tier: 'free' | 'pro') {
    return this.request<{ message: string }>(`/admin/users/${userId}/tier`, {
      method: 'PUT',
      body: JSON.stringify({ tier }),
    });
  }

  async getUserSettings(userId: string) {
    return this.request<{
      id: string;
      email: string;
      displayName: string;
      role: 'admin' | 'user';
      aiTier: 'free' | 'pro';
      proUsageCount: number;
      proUsageLimit: number;
      proUsagePeriodEnd: string | null;
      preferences: Record<string, unknown>;
      bio: string;
      location: string;
      profileSlug: string | null;
      aiConfig: {
        provider: string;
        endpoint: string | null;
        deployment: string | null;
        apiVersion: string | null;
        model: string | null;
        apiKeyMasked: string;
      } | null;
    }>(`/admin/users/${userId}/settings`);
  }

  async updateUserSettings(userId: string, settings: {
    displayName?: string;
    email?: string;
    role?: 'admin' | 'user';
    aiTier?: 'free' | 'pro';
    proUsageLimit?: number;
    preferences?: Record<string, unknown>;
    bio?: string;
    location?: string;
    profileSlug?: string;
    aiConfig?: {
      provider: string;
      apiKey: string;
      endpoint?: string;
      deployment?: string;
      apiVersion?: string;
      model?: string;
    } | null;
  }) {
    return this.request<{ message: string }>(`/admin/users/${userId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async resetUserPassword(userId: string, password: string) {
    return this.request<{ success: boolean }>(`/admin/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async banUser(userId: string) {
    return this.request<void>(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Kept for backwards compat
  async removeUser(userId: string) {
    return this.banUser(userId);
  }

  // Showroom
  async toggleShowroom(itemId: string, showcase?: boolean) {
    return this.request<{ showcase: boolean }>(`/inventory/${itemId}/showroom`, {
      method: 'PATCH',
      body: JSON.stringify(showcase !== undefined ? { showcase } : {}),
    });
  }

  async listShowroom(query: Record<string, string> = {}) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    });
    const qs = params.toString();
    return this.request<PaginatedResponse<InventoryItem>>(`/showroom${qs ? `?${qs}` : ''}`);
  }

  async getShowroomTicker(vaultType?: string) {
    const qs = vaultType ? `?vaultType=${encodeURIComponent(vaultType)}` : '';
    return this.request<{ data: ShowroomTickerItem[] }>(`/showroom/ticker${qs}`);
  }

  // Export
  getExportCsvUrl(filters: Record<string, string> = {}) {
    const params = new URLSearchParams(filters);
    const qs = params.toString();
    return `${BASE_URL}/export/csv${qs ? `?${qs}` : ''}`;
  }

  getExportJsonUrl(filters: Record<string, string> = {}) {
    const params = new URLSearchParams(filters);
    const qs = params.toString();
    return `${BASE_URL}/export/json${qs ? `?${qs}` : ''}`;
  }

  // Backup & Restore (desktop only)
  async createBackup() {
    return this.request<{ name: string; sizeBytes: number; createdAt: string; migrationVersion: number }>('/backup', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async listBackups() {
    return this.request<{
      data: Array<{ name: string; sizeBytes: number; createdAt: string; migrationVersion: number | null }>;
      currentMigrationVersion: number;
    }>('/backups');
  }

  async deleteBackup(name: string) {
    return this.request<{ success: boolean }>('/backup/delete', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async restoreBackup(name: string) {
    return this.request<{ success: boolean; message: string; safetyBackup: string }>('/backup/restore', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  // Taxonomy
  async getTaxonomySuggestions(field: string, category?: string, platform?: string, vaultId?: string) {
    const params = new URLSearchParams({ field });
    if (category) params.set('category', category);
    if (platform) params.set('platform', platform);
    if (vaultId) params.set('vaultId', vaultId);
    return this.request<TaxonomySuggestionsResponse>(`/taxonomy/suggestions?${params.toString()}`);
  }

  // Item Links (Feature 4)
  async getItemLinks(itemId: string) {
    return this.request<{ data: ItemLinkWithDetails[] }>(`/inventory/${itemId}/links`);
  }

  async createItemLink(itemId: string, data: { peripheralItemId: string; linkType: string; notes?: string }) {
    return this.request<any>(`/inventory/${itemId}/links`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteItemLink(itemId: string, linkId: string) {
    return this.request<void>(`/inventory/${itemId}/links/${linkId}`, {
      method: 'DELETE',
    });
  }

  async getDeletePreflight(itemId: string) {
    return this.request<DeletePreflightResult>(`/inventory/${itemId}/delete-preflight`);
  }

  // Capacitor Map (Feature 1)
  async getCapacitorMap(itemId: string) {
    return this.request<any>(`/inventory/${itemId}/capacitor-map`);
  }

  async createCapacitorMap(itemId: string, data: any) {
    return this.request<any>(`/inventory/${itemId}/capacitor-map`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCapacitorMap(itemId: string, data: any) {
    return this.request<any>(`/inventory/${itemId}/capacitor-map`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateCapStatus(itemId: string, designator: string, data: any) {
    return this.request<any>(`/inventory/${itemId}/capacitor-map/caps/${encodeURIComponent(designator)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Library (Feature 5)
  async listLibraryFiles(query: Record<string, any> = {}) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== false) {
        params.set(key, String(value));
      }
    });
    const qs = params.toString();
    return this.request<any>(`/library${qs ? `?${qs}` : ''}`);
  }

  async uploadLibraryFile(data: any) {
    return this.request<any>('/library', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteLibraryFile(fileId: string) {
    return this.request<void>(`/library/${fileId}`, {
      method: 'DELETE',
    });
  }

  async getItemFiles(itemId: string) {
    return this.request<{ data: any[] }>(`/inventory/${itemId}/files`);
  }

  // Retrobright (Feature 3)
  async getRetroBrightProjects(itemId: string) {
    return this.request<{ data: any[] }>(`/inventory/${itemId}/retrobright`);
  }

  async createRetroBright(itemId: string, data: any) {
    return this.request<any>(`/inventory/${itemId}/retrobright`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getRetroBrightProject(projectId: string) {
    return this.request<any>(`/retrobright/${projectId}`);
  }

  async updateRetroBright(projectId: string, data: any) {
    return this.request<any>(`/retrobright/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async startRetroBrightSession(projectId: string, notes?: string) {
    return this.request<any>(`/retrobright/${projectId}/sessions/start`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async stopRetroBrightSession(projectId: string) {
    return this.request<any>(`/retrobright/${projectId}/sessions/stop`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Health (Feature 6)
  async getHealthSummary(vaultId?: string) {
    const qs = vaultId ? `?vaultId=${vaultId}` : '';
    return this.request<CollectionHealthSummary>(`/health/summary${qs}`);
  }

  async getItemAlerts(itemId: string) {
    return this.request<{ data: any[] }>(`/inventory/${itemId}/alerts`);
  }

  async createAlert(itemId: string, data: any) {
    return this.request<any>(`/inventory/${itemId}/alerts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resolveAlert(alertId: string, resolved: boolean) {
    return this.request<any>(`/maintenance/alerts/${alertId}/resolve`, {
      method: 'PATCH',
      body: JSON.stringify({ resolved }),
    });
  }

  // Community: Public Profiles
  async getPublicProfile(slug: string) {
    return this.request<PublicProfile>(`/profiles/${slug}`);
  }

  async getProfileItems(slug: string, query: Record<string, string> = {}) {
    const params = new URLSearchParams(query);
    const qs = params.toString();
    return this.request<PaginatedResponse<InventoryItem>>(`/profiles/${slug}/items${qs ? `?${qs}` : ''}`);
  }

  async getProfileWanted(slug: string) {
    return this.request<{ data: WantedItem[] }>(`/profiles/${slug}/wanted`);
  }

  async updatePublicProfile(data: UpdatePublicProfileInput) {
    return this.request<PublicProfile>('/profile/public', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async uploadAvatar(fileOrBlob: File | Blob, contentType?: string) {
    const buffer = await fileOrBlob.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );
    const type = contentType || (fileOrBlob instanceof File ? fileOrBlob.type : 'image/jpeg');
    const name = fileOrBlob instanceof File ? fileOrBlob.name : 'avatar.jpg';

    return this.request<{ avatarUrl: string }>('/profile/avatar', {
      method: 'POST',
      body: JSON.stringify({ data: base64, contentType: type, filename: name }),
    });
  }

  async deleteAvatar() {
    return this.request<void>('/profile/avatar', { method: 'DELETE' });
  }

  // Community: Listing Status
  async updateListingStatus(itemId: string, data: UpdateListingStatusInput) {
    return this.request<{ listingStatus: string; askingPrice: string | null; listingCurrency: string }>(
      `/inventory/${itemId}/listing`,
      { method: 'PATCH', body: JSON.stringify(data) }
    );
  }

  // Community: Wanted Items
  async listWantedItems(vaultId?: string) {
    const qs = vaultId ? `?vaultId=${vaultId}` : '';
    return this.request<{ data: WantedItem[] }>(`/wanted${qs}`);
  }

  async createWantedItem(data: CreateWantedItemInput, vaultId?: string) {
    const qs = vaultId ? `?vaultId=${vaultId}` : '';
    return this.request<WantedItem>(`/wanted${qs}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWantedItem(id: string, data: UpdateWantedItemInput) {
    return this.request<WantedItem>(`/wanted/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteWantedItem(id: string) {
    return this.request<void>(`/wanted/${id}`, { method: 'DELETE' });
  }

  // Community: Offers
  async listOffers(query: Record<string, string> = {}) {
    const params = new URLSearchParams(query);
    const qs = params.toString();
    return this.request<PaginatedResponse<Offer>>(`/offers${qs ? `?${qs}` : ''}`);
  }

  async getOffer(id: string) {
    return this.request<Offer>(`/offers/${id}`);
  }

  async createOffer(data: CreateOfferInput) {
    return this.request<Offer>('/offers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async respondToOffer(id: string, action: 'accept' | 'decline') {
    return this.request<Offer>(`/offers/${id}/respond`, {
      method: 'PATCH',
      body: JSON.stringify({ action }),
    });
  }

  async withdrawOffer(id: string) {
    return this.request<Offer>(`/offers/${id}/withdraw`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async dismissOffer(id: string) {
    return this.request<{ success: boolean }>(`/offers/${id}/dismiss`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  // Notifications
  async listNotifications(query: Record<string, string> = {}) {
    const params = new URLSearchParams(query);
    const qs = params.toString();
    return this.request<PaginatedResponse<Notification> & { unreadCount: number }>(
      `/notifications${qs ? `?${qs}` : ''}`
    );
  }

  async getUnreadCount() {
    return this.request<{ unreadCount: number }>('/notifications/unread-count');
  }

  async markNotificationRead(id: string) {
    return this.request<{ success: boolean }>(`/notifications/${id}/read`, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async markAllNotificationsRead() {
    return this.request<{ success: boolean }>('/notifications/read-all', {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  // Favorites
  async addFavorite(itemId: string) {
    return this.request<{ success: boolean }>(`/favorites/${itemId}`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async removeFavorite(itemId: string) {
    return this.request<void>(`/favorites/${itemId}`, { method: 'DELETE' });
  }

  async listFavorites(query: Record<string, string> = {}) {
    const params = new URLSearchParams(query);
    const qs = params.toString();
    return this.request<PaginatedResponse<Favorite>>(`/favorites${qs ? `?${qs}` : ''}`);
  }

  async getFavoriteStatus(itemId: string) {
    return this.request<{ isFavorited: boolean; count: number }>(`/favorites/${itemId}/status`);
  }

  // ─── License Admin ───────────────────────────────────────────────────────

  async listAdminLicenses(query: Record<string, string | number> = {}) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') params.set(key, String(value));
    });
    const qs = params.toString();
    return this.request<{
      data: Array<{
        id: string;
        email: string;
        licenseKey: string;
        issuedAt: string;
        revokedAt: string | null;
        notes: string | null;
        issuedByName: string | null;
        revokedByName: string | null;
        activationCount: number;
        lastActivatedAt: string | null;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/admin/licenses${qs ? `?${qs}` : ''}`);
  }

  async getLicenseStats() {
    return this.request<{
      total: number;
      active: number;
      revoked: number;
      activations: number;
    }>('/admin/licenses/stats');
  }

  async issueLicense(email: string, notes?: string) {
    return this.request<{ licenseKey: string }>('/admin/licenses', {
      method: 'POST',
      body: JSON.stringify({ email, notes }),
    });
  }

  async revokeLicense(licenseId: string) {
    return this.request<{ revoked: boolean }>(`/admin/licenses/${licenseId}/revoke`, {
      method: 'PATCH',
    });
  }

  async deleteLicense(licenseId: string) {
    return this.request<void>(`/admin/licenses/${licenseId}`, {
      method: 'DELETE',
    });
  }

  // AI Tier & Subscription
  async analyzeItem(itemId: string) {
    return this.request<{
      analysis: {
        name: string;
        category: string;
        platform: string;
        subcategory: string | null;
        condition: string;
        videoSystem: string | null;
        description: string;
        confidence: 'high' | 'medium' | 'low';
        cpu?: string | null;
        memory?: string | null;
        launchDate?: string | null;
        launchPrice?: string | null;
        launchPriceCurrency?: string | null;
        launchPriceInflationAdjusted?: string | null;
      };
      itemId: string;
    }>(`/items/${itemId}/analyze`, {
      method: 'POST',
    });
  }

  async getAiUsage() {
    return this.request<{
      tier: string;
      usageCount: number;
      usageLimit: number;
      periodEnd: string | null;
      remaining: number;
    }>('/user/ai-usage');
  }

  async getSubscription() {
    return this.request<{
      tier: string;
      subscriptionActive: boolean;
      usage: {
        count: number;
        limit: number;
        periodEnd: string | null;
        remaining: number;
      };
    }>('/subscription');
  }

  async createCheckout(billing: 'monthly' | 'yearly' = 'monthly') {
    return this.request<{ checkoutUrl: string }>('/subscription/checkout', {
      method: 'POST',
      body: JSON.stringify({ billing }),
    });
  }

  // Download Links
  async getDownloadLinks() {
    return this.request<{
      version: string | null;
      mac: string;
      windows: string;
      linux: { deb: string; appImage: string };
    }>('/downloads');
  }

  async createDesktopCheckout(email?: string) {
    return this.request<{ checkoutUrl: string }>('/license/checkout', {
      method: 'POST',
      body: JSON.stringify(email ? { email } : {}),
    });
  }

  // --- Feedback ---
  async createFeedback(data: { type: string; title: string; description: string }) {
    return this.request<any>('/feedback', { method: 'POST', body: JSON.stringify(data) });
  }

  async listFeedback(query: Record<string, string> = {}) {
    const qs = new URLSearchParams(query).toString();
    return this.request<any>(`/feedback${qs ? `?${qs}` : ''}`);
  }

  async getFeedback(id: string) {
    return this.request<any>(`/feedback/${id}`);
  }

  async adminListFeedback(query: Record<string, string> = {}) {
    const qs = new URLSearchParams(query).toString();
    return this.request<any>(`/admin/feedback${qs ? `?${qs}` : ''}`);
  }

  async adminFeedbackStats() {
    return this.request<Record<string, number>>('/admin/feedback/stats');
  }

  async adminUpdateFeedbackStatus(id: string, status: string) {
    return this.request<any>(`/admin/feedback/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
  }

  async adminReplyFeedback(id: string, reply: string) {
    return this.request<any>(`/admin/feedback/${id}/reply`, { method: 'POST', body: JSON.stringify({ reply }) });
  }
}

export const apiClient = new ApiClient();
